import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const EnhancedModal = ({
  children,
  onClose,
  size = 'md',
  showCloseButton = true,
  className = '',
  overlayClassName = '',
  contentClassName = '',
  usePortal = true
}) => {
  const { theme, isLoading } = useTheme();

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl'
  };

  // Vérifier la classe HTML pour détecter le mode dark effectif
  // Cela fonctionne même quand theme === 'system'
  const isDarkMode = isLoading
    ? typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    : document.documentElement.classList.contains('dark');

  const modalContent = (
    <div className={`fixed inset-0 flex justify-center items-center z-[10000] p-4 ${overlayClassName}`}>
      {/* Overlay avec gestion du thème */}
      <div
        className={`absolute inset-0 transition-colors duration-300 ${isDarkMode ? 'bg-black bg-opacity-70' : 'bg-black bg-opacity-60'
          }`}
        onClick={onClose}
      />

      {/* Contenu du modal */}
      <div
        className={`
          relative rounded-xl shadow-2xl transform transition-all duration-300
          ${sizeClasses[size]} 
          w-full mx-auto
          ${isDarkMode
            ? 'bg-gray-900 text-gray-100 border border-gray-700 shadow-2xl'
            : 'bg-white text-gray-900 border border-gray-200 shadow-xl'
          }
          ${className}
        `}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            className={`
              absolute -top-3 -right-3 z-10 rounded-full p-1.5 transition-all duration-200
              hover:scale-110 active:scale-95 border
              ${isDarkMode
                ? 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700 hover:text-white hover:border-gray-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100 hover:text-gray-800 hover:border-gray-400'
              }
              shadow-lg
            `}
          >
            <X size={18} />
          </button>
        )}
        <div className={contentClassName}>
          {children}
        </div>
      </div>
    </div>
  );

  if (usePortal) {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

// Composant pour les en-têtes de modal
export const ModalHeader = ({ children, className = '' }) => {
  const { theme, isLoading } = useTheme();
  const isDarkMode = isLoading
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : document.documentElement.classList.contains('dark');

  return (
    <div className={`
      px-6 py-4 border-b
      ${isDarkMode
        ? 'border-gray-700 bg-gray-800 text-gray-100'
        : 'border-gray-200 bg-gray-50 text-gray-900'
      }
      ${className}
    `}>
      <h2 className="text-xl font-bold">{children}</h2>
    </div>
  );
};

// Composant pour le contenu de modal
export const ModalBody = ({ children, className = '', maxHeight = 'max-h-[70vh]' }) => {
  const { theme, isLoading } = useTheme();
  const isDarkMode = isLoading
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : document.documentElement.classList.contains('dark');

  return (
    <div className={`px-6 py-4 ${maxHeight} overflow-y-auto ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} ${className}`}>
      {children}
    </div>
  );
};

// Composant pour les pieds de modal
export const ModalFooter = ({ children, className = '' }) => {
  const { theme, isLoading } = useTheme();
  const isDarkMode = isLoading
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : document.documentElement.classList.contains('dark');

  return (
    <div className={`
      px-6 py-4 border-t
      ${isDarkMode
        ? 'border-gray-700 bg-gray-800'
        : 'border-gray-200 bg-gray-50'
      }
      ${className}
    `}>
      <div className="flex justify-end space-x-3">
        {children}
      </div>
    </div>
  );
};

// Boutons de modal standardisés
export const ModalButton = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
  ...props
}) => {
  const { theme, isLoading } = useTheme();
  const isDarkMode = isLoading
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : document.documentElement.classList.contains('dark');

  const baseClasses = `
    font-medium rounded-lg transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    active:scale-95
  `;

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base'
  };

  const variantClasses = {
    primary: isDarkMode
      ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 focus:ring-offset-gray-900'
      : 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-400 focus:ring-offset-white',
    secondary: isDarkMode
      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-gray-500 focus:ring-offset-gray-900'
      : 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400 focus:ring-offset-white',
    success: isDarkMode
      ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 focus:ring-offset-gray-900'
      : 'bg-green-500 text-white hover:bg-green-600 focus:ring-green-400 focus:ring-offset-white',
    danger: isDarkMode
      ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 focus:ring-offset-gray-900'
      : 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-400 focus:ring-offset-white',
    warning: isDarkMode
      ? 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500 focus:ring-offset-gray-900'
      : 'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-400 focus:ring-offset-white'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${baseClasses}
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  );
};

export default EnhancedModal;
